// src/pages/LoginPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import '../styles/LoginPage.css';
import growuptechlinktree from '../images/growuptechlinktree.png';

async function ensureUserDoc(user) {
  if (!user) return;
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // أنشئ وثيقة مبدئية لأول مرة
    await setDoc(ref, {
      uid: user.uid,
      email: user.email || '',
      username: `user_${user.uid.substring(0, 6)}`,
      displayName: user.displayName || 'New User',
      profileType: 'personal',
      theme: 'light',
      isAdmin: false,
      photoURL: user.photoURL || '',
      links: {},
    });
  }
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // حالات مودال إعادة التعيين
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  const navigate = useNavigate();

  // معالجة نتيجة redirect لـ Google (مهم للموبايل وسفاري)
  useEffect(() => {
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          // إنشاء وثيقة المستخدم لو أول مرة
          await ensureUserDoc(result.user);
        }
      } catch (e) {
        console.error(e);
        // لا توقف الصفحة بسبب خطأ redirect
      }
    })();
  }, []);

  // التوجّه حسب الدور + تأكيد وجود وثيقة المستخدم لحالات Google popup أيضاً
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoading(true);
        try {
          await ensureUserDoc(user); // يتأكد دومًا أن الوثيقة موجودة
          const userDocRef = doc(db, 'users', user.uid);
          const snap = await getDoc(userDocRef);
          if (snap.exists() && snap.data().isAdmin) {
            navigate('/admin/users');
          } else {
            navigate('/dashboard');
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        const user = cred.user;
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          username: `user_${user.uid.substring(0, 6)}`,
          displayName: 'New User',
          profileType: 'personal',
          theme: 'light',
          isAdmin: false,
          photoURL: '',
          links: {},
        });
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  // Google Sign-In (Popup أولاً، ثم Redirect كباك أب)
  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      // (اختياري) مزيد من المعلومات العامة
      provider.setCustomParameters({ prompt: 'select_account' });
      // جرّب popup
      const res = await signInWithPopup(auth, provider);
      if (res?.user) {
        await ensureUserDoc(res.user);
      }
      // التنقّل سيحصل عبر onAuthStateChanged
    } catch (e) {
      // في حال حظر الـ popup، نستخدم redirect كخطة بديلة
      const needsRedirect =
        e?.code === 'auth/popup-blocked' ||
        e?.code === 'auth/popup-closed-by-user' ||
        e?.message?.toLowerCase?.().includes('popup') ||
        /safari|iphone|ipad|ipod/i.test(navigator.userAgent);

      if (needsRedirect) {
        try {
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await signInWithRedirect(auth, provider);
          return; // سيعود بعدها عبر getRedirectResult + onAuthStateChanged
        } catch (e2) {
          console.error(e2);
          setError(e2.message || 'Failed to redirect to Google sign-in.');
        }
      } else {
        console.error(e);
        setError(e.message || 'Google sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  // فتح/إغلاق مودال إعادة التعيين
  const openReset = () => {
    setResetMsg('');
    setResetEmail(email || '');
    setResetOpen(true);
  };
  const closeReset = () => {
    if (resetLoading) return;
    setResetOpen(false);
    setResetMsg('');
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetMsg('');
    const targetEmail = (resetEmail || '').trim();
    if (!targetEmail) {
      setResetMsg('Please enter your email address.');
      return;
    }
    try {
      setResetLoading(true);
      await sendPasswordResetEmail(auth, targetEmail);
      setResetMsg('Password reset email sent. Please check your inbox (and Spam/Promotions).');
    } catch (err) {
      const code = err?.code || '';
      if (code.includes('auth/invalid-email')) {
        setResetMsg('Invalid email address.');
      } else if (code.includes('auth/user-not-found')) {
        setResetMsg('No user found with this email.');
      } else {
        setResetMsg(err.message || 'Failed to send reset email.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // إغلاق بالـ Esc
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && resetOpen && !resetLoading) closeReset();
  }, [resetOpen, resetLoading]);

  useEffect(() => {
    if (resetOpen) {
      document.addEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [resetOpen, onKeyDown]);

  if (loading) {
    return <div className="center-loader">Loading...</div>;
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* شعار */}
        <div className="login-logo">
          <img src= {growuptechlinktree} alt="Your Logo" />
        </div>

        <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
        <p>{isLogin ? 'Login to manage your profile' : 'Sign up to get started'}</p>

        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min. 6 characters)"
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            required
            minLength={6}
          />
          {isLogin && (
            <div className="forgot-wrap">
              <button type="button" className="link-btn" onClick={openReset}>
                Forgot password?
              </button>
            </div>
          )}
          <button type="submit" className="login-button" disabled={loading}>
            {isLogin ? 'Login' : 'Sign Up'}
          </button>
        </form>

        {error && <p className="error-message">{error}</p>}

        {/* فاصل وأزرار دخول اجتماعي */}
        <div className="social-divider">
          <span>OR</span>
        </div>

        <div className="social-logins">
          <button
            type="button"
            className="social-btn social-btn-google"
            onClick={handleGoogleSignIn}
            disabled={loading}
            aria-label="Sign in with Google"
          >
            Sign in with Google
          </button>
          {/* يمكن إضافة فيسبوك لاحقًا */}
        </div>

        <button
          onClick={() => setIsLogin(!isLogin)}
          className="toggle-button"
          disabled={loading}
        >
          {isLogin ? 'Need an account? Sign Up' : 'Have an account? Login'}
        </button>
      </div>

      {/* مودال إعادة تعيين كلمة المرور */}
      {resetOpen && (
        <div className="reset-overlay" onClick={closeReset}>
          <div
            className="reset-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Reset password"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Reset your password</h3>
            <p className="reset-hint">
              Enter your account email and we’ll send you a password reset link.
            </p>

            <form onSubmit={handleReset} className="reset-form">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Your email"
                autoFocus
                required
              />
              <div className="reset-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeReset}
                  disabled={resetLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Sending…' : 'Send reset link'}
                </button>
              </div>
            </form>

            {resetMsg && <p className="reset-message">{resetMsg}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
